import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { collection, onSnapshot } from "firebase/firestore";
import {
  format,
  startOfDay,
  subDays,
  eachDayOfInterval,
  isSameDay,
} from "date-fns";
import {
  FaDumbbell,
  FaBook,
  FaBed,
  FaBrain,
  FaHeart,
  FaChartLine,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaFire,
  FaRegCalendarCheck,
} from "react-icons/fa";
import { motion } from "framer-motion";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase/config";
import Navbar from "../components/Navbar";

const icons = { FaDumbbell, FaBook, FaBed, FaBrain, FaHeart };

// ===== Styled Components =====
const Page = styled.div`
  width: 100%;
  padding: 1.5rem;
  box-sizing: border-box;
  color: var(--fontPrimaryColor);
  background: var(--primaryBackgroundColor);
  min-height: 100vh;
  transition: 0.5s;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  margin-top: 70px;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  margin: 0;
  max-width: 600px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  transition: 0.5s;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainPanel = styled(motion.div)`
  background: var(--secondaryBackgroundColor);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: 0.5s;
`;

const SidePanel = styled(MainPanel)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const StatCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  transition: 0.5s;
`;

const StatCard = styled(motion.div)`
  background: var(--secondaryBackgroundColor);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
  }
  transition: 0.5s;
`;

const StatCardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;

  &.success {
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  }

  &.failure {
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  }

  &.rate {
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  }

  &.habits {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  }
`;

const StatCardValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const StatCardLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
`;

const TrendBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 8px;
  transition: 0.5s;

  &.up {
    background: #dcfce7;
    color: #166534;
  }

  &.down {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const HabitSelector = styled.div`
  margin-bottom: 2rem;
  transition: 0.5s;
`;

const SelectLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-weight: 600;
  transition: 0.5s;
`;

const CustomSelect = styled.select`
  width: 100%;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: var(--thirdBackgroundColor);
  font-size: 1rem;
  font-weight: 500;
  outline: none;
  color: var(--fontPrimaryColor);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  transition: 0.5s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HabitHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  transition: 0.5s;
`;

const HabitIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--thirdBackgroundColor);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
  transition: 0.5s;
`;

const HabitTitle = styled.div`
  flex: 1;
`;

const HabitName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
`;

const HabitMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: 0.5s;
`;

const ProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProgressCard = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 1.25rem;
  transition: 0.5s;
`;

const ProgressTitle = styled.div`
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ProgressValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
`;

const CalendarSection = styled.div`
  margin-top: 2rem;
`;

const CalendarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(14, 1fr);
  gap: 8px;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(7, 1fr);
  }
`;

const DayCell = styled(motion.div)`
  aspect-ratio: 1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;

  &.completed {
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
    color: white;
  }

  &.failed {
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
    color: white;
  }

  &.neutral {
    background: #f1f5f9;
    color: #64748b;
  }

  &.today {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #8b7064;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 4px;

  &.completed {
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  }

  &.failed {
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  }

  &.neutral {
    background: #f1f5f9;
  }
`;

const StreakCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const StreakValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: white;
`;

const StreakLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const InsightCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const InsightText = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
`;

// ===== Helper Functions =====
function safeIcon(name) {
  return icons[name] || FaDumbbell;
}

function isHabitRequiredOn(habit, date) {
  const type =
    habit.recurrenceType ||
    (habit.days && habit.days.length ? "SPECIFIC_DAYS" : "DAILY");

  if (type === "DAILY") return true;
  if (type === "SPECIFIC_DAYS") {
    const dayName = format(date, "EEEE");
    return (habit.days || []).includes(dayName);
  }
  if (type === "FLEXIBLE_WEEKLY") return false;
  return true;
}

function getHabitStatusForKey(habit, dateKey) {
  const status = habit.statusByDate?.[dateKey];
  if (status === "completed" || status === "failed") return status;
  return habit.completedDates?.[dateKey] ? "completed" : null;
}

function computeHabitStats(habit, today) {
  const startDate = habit.startDate?.toDate
    ? habit.startDate.toDate()
    : habit.startDate
    ? new Date(habit.startDate)
    : new Date(0);

  const start = startOfDay(startDate);
  const end = today;

  let successes = 0;
  let failures = 0;
  let requiredTotal = 0;
  let currentStreak = 0;
  let bestStreak = 0;
  let tempStreak = 0;

  const days = eachDayOfInterval({ start, end });
  for (const d of days) {
    const dateKey = format(d, "yyyy-MM-dd");
    const required = isHabitRequiredOn(habit, d);
    const recordedStatus = getHabitStatusForKey(habit, dateKey);
    const isPastDay = d.getTime() < today.getTime();

    if (required) requiredTotal += 1;

    if (recordedStatus === "completed") {
      successes += 1;
      tempStreak += 1;
      bestStreak = Math.max(bestStreak, tempStreak);
      if (isSameDay(d, today) || isSameDay(d, subDays(today, 1))) {
        currentStreak = tempStreak;
      }
      continue;
    }

    if (recordedStatus === "failed") {
      failures += 1;
      tempStreak = 0;
      continue;
    }

    if (!recordedStatus && required && isPastDay) {
      failures += 1;
      tempStreak = 0;
    } else {
      tempStreak = 0;
    }
  }

  const attempts = successes + failures;
  const successRate = attempts ? Math.round((successes / attempts) * 100) : 0;

  return {
    successes,
    failures,
    requiredTotal,
    successRate,
    currentStreak,
    bestStreak,
  };
}

// ===== Main Component =====
export default function HabitAnalysis({ theme, switchTheme }) {
  const { userId } = useAuth();
  const [habits, setHabits] = useState([]);
  const [selectedHabitId, setSelectedHabitId] = useState("");

  useEffect(() => {
    if (!userId) return;
    const col = collection(db, "users", userId, "habits");
    const unsub = onSnapshot(col, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHabits(data);
      setSelectedHabitId((prev) => prev || (data[0]?.id ?? ""));
    });
    return () => unsub();
  }, [userId]);

  const today = useMemo(() => startOfDay(new Date()), []);

  const perHabit = useMemo(() => {
    const map = new Map();
    habits.forEach((h) => map.set(h.id, computeHabitStats(h, today)));
    return map;
  }, [habits, today]);

  const overall = useMemo(() => {
    let successes = 0;
    let failures = 0;
    let totalSuccessRate = 0;
    let validHabits = 0;

    habits.forEach((h) => {
      const stats = perHabit.get(h.id);
      if (!stats) return;
      successes += stats.successes;
      failures += stats.failures;
      if (stats.successRate > 0) {
        totalSuccessRate += stats.successRate;
        validHabits++;
      }
    });

    const attempts = successes + failures;
    const avgSuccessRate = validHabits
      ? Math.round(totalSuccessRate / validHabits)
      : 0;
    const overallSuccessRate = attempts
      ? Math.round((successes / attempts) * 100)
      : 0;

    return {
      successRate: overallSuccessRate,
      avgSuccessRate,
      successes,
      failures,
      habitCount: habits.length,
    };
  }, [habits, perHabit]);

  const selectedHabit = useMemo(
    () => habits.find((h) => h.id === selectedHabitId) || null,
    [habits, selectedHabitId]
  );

  const selectedStats = useMemo(() => {
    if (!selectedHabit) return null;
    return perHabit.get(selectedHabit.id) || null;
  }, [perHabit, selectedHabit]);

  const last14 = useMemo(() => {
    if (!selectedHabit) return [];
    const end = today;
    const start = subDays(end, 13);
    const days = eachDayOfInterval({ start, end });

    return days.map((d) => {
      const key = format(d, "yyyy-MM-dd");
      const recorded = getHabitStatusForKey(selectedHabit, key);
      const required = isHabitRequiredOn(selectedHabit, d);
      const isPastDay = d.getTime() < today.getTime();
      const isToday = isSameDay(d, today);

      const status =
        recorded || (required && isPastDay ? "auto_failed" : "neutral");

      return {
        key,
        status,
        date: d,
        isToday,
        displayDate: format(d, "d"),
        dayName: format(d, "EEE"),
      };
    });
  }, [selectedHabit, today]);

  const SelectedIcon = selectedHabit
    ? safeIcon(selectedHabit.icon)
    : FaChartLine;

  const getInsight = () => {
    if (!selectedStats) return "Select a habit to see insights";

    if (selectedStats.successRate >= 80) {
      return "🔥 You're crushing it! Keep up the amazing consistency.";
    } else if (selectedStats.successRate >= 60) {
      return "📈 Good progress! Try to eliminate those missed days.";
    } else if (selectedStats.successRate >= 40) {
      return "💪 You're making progress. Focus on building consistency.";
    } else {
      return "🌟 Every journey starts somewhere. Try committing to just 2 days this week.";
    }
  };

  return (
    <Page>
      <Navbar theme={theme} switchTheme={switchTheme} />
      <Header>
        <Title>Habit Analytics Dashboard</Title>
        <Subtitle>
          Track your progress, identify patterns, and optimize your habits for
          success
        </Subtitle>
      </Header>

      <StatCardsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatCardIcon className="rate">
            <FaChartLine />
          </StatCardIcon>
          <StatCardValue>{overall.successRate}%</StatCardValue>
          <StatCardLabel>Overall Success Rate</StatCardLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCardIcon className="success">
            <FaCheckCircle />
          </StatCardIcon>
          <StatCardValue>{overall.successes}</StatCardValue>
          <StatCardLabel>Total Successes</StatCardLabel>
          {overall.successes > 0 && (
            <TrendBadge className="up">
              +{Math.round(overall.successes / 30)}/month
            </TrendBadge>
          )}
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatCardIcon className="failure">
            <FaTimesCircle />
          </StatCardIcon>
          <StatCardValue>{overall.failures}</StatCardValue>
          <StatCardLabel>Total Failures</StatCardLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StatCardIcon className="habits">
            <FaRegCalendarCheck />
          </StatCardIcon>
          <StatCardValue>{overall.habitCount}</StatCardValue>
          <StatCardLabel>Active Habits</StatCardLabel>
        </StatCard>
      </StatCardsGrid>

      <DashboardGrid>
        <MainPanel
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <HabitSelector>
            <SelectLabel>
              <FaChartLine />
              Analyze Specific Habit
            </SelectLabel>
            <CustomSelect
              value={selectedHabitId}
              onChange={(e) => setSelectedHabitId(e.target.value)}
              disabled={habits.length === 0}
            >
              {habits.length === 0 ? (
                <option value="">No habits found</option>
              ) : (
                habits.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))
              )}
            </CustomSelect>
          </HabitSelector>

          {selectedHabit && selectedStats && (
            <>
              <HabitHeader>
                <HabitIconWrapper>
                  <SelectedIcon />
                </HabitIconWrapper>
                <HabitTitle>
                  <HabitName>{selectedHabit.name}</HabitName>
                  <HabitMeta>
                    <MetaItem>
                      <FaCalendarAlt />
                      Started{" "}
                      {format(
                        selectedHabit.startDate?.toDate?.() || new Date(),
                        "MMM d, yyyy"
                      )}
                    </MetaItem>
                    <MetaItem>
                      <FaFire />
                      {selectedStats.currentStreak} day streak
                    </MetaItem>
                  </HabitMeta>
                </HabitTitle>
              </HabitHeader>

              <ProgressGrid>
                <ProgressCard>
                  <ProgressTitle>Success Rate</ProgressTitle>
                  <ProgressValue>{selectedStats.successRate}%</ProgressValue>
                </ProgressCard>
                <ProgressCard>
                  <ProgressTitle>Current Streak</ProgressTitle>
                  <ProgressValue>{selectedStats.currentStreak}</ProgressValue>
                </ProgressCard>
                <ProgressCard>
                  <ProgressTitle>Best Streak</ProgressTitle>
                  <ProgressValue>{selectedStats.bestStreak}</ProgressValue>
                </ProgressCard>
              </ProgressGrid>

              <ProgressGrid>
                <ProgressCard>
                  <ProgressTitle>Total Successes</ProgressTitle>
                  <ProgressValue>{selectedStats.successes}</ProgressValue>
                </ProgressCard>
                <ProgressCard>
                  <ProgressTitle>Total Failures</ProgressTitle>
                  <ProgressValue>{selectedStats.failures}</ProgressValue>
                </ProgressCard>
                <ProgressCard>
                  <ProgressTitle>Required Days</ProgressTitle>
                  <ProgressValue>{selectedStats.requiredTotal}</ProgressValue>
                </ProgressCard>
              </ProgressGrid>

              <CalendarSection>
                <CalendarTitle>
                  <FaCalendarAlt />
                  Last 14 Days
                </CalendarTitle>
                <CalendarGrid>
                  {last14.map((day) => (
                    <DayCell
                      key={day.key}
                      className={`${
                        day.status === "completed"
                          ? "completed"
                          : day.status === "failed" ||
                            day.status === "auto_failed"
                          ? "failed"
                          : "neutral"
                      } 
                                   ${day.isToday ? "today" : ""}`}
                      title={`${format(day.date, "MMM d, yyyy")}: ${
                        day.status
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {day.displayDate}
                    </DayCell>
                  ))}
                </CalendarGrid>

                <Legend>
                  <LegendItem>
                    <LegendDot className="completed" />
                    Completed
                  </LegendItem>
                  <LegendItem>
                    <LegendDot className="failed" />
                    Failed/Missed
                  </LegendItem>
                  <LegendItem>
                    <LegendDot className="neutral" />
                    Neutral/Future
                  </LegendItem>
                  <LegendItem>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "4px",
                        border: "2px solid #3b82f6",
                      }}
                    />
                    Today
                  </LegendItem>
                </Legend>
              </CalendarSection>
            </>
          )}
        </MainPanel>

        <SidePanel
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          {selectedStats && (
            <>
              <StreakCard>
                <FaFire
                  size={48}
                  style={{ marginBottom: "1rem", opacity: 0.9 }}
                />
                <StreakValue>{selectedStats.currentStreak}</StreakValue>
                <StreakLabel>Current Streak</StreakLabel>
              </StreakCard>

              <InsightCard>
                <InsightText>{getInsight()}</InsightText>
              </InsightCard>

              <InsightCard>
                <InsightText>
                  <strong>📊 Your Stats:</strong>
                  <br />• {selectedStats.successRate}% success rate
                  <br />• Best streak: {selectedStats.bestStreak} days
                  <br />• {selectedStats.successes} successful days
                </InsightText>
              </InsightCard>

              <InsightCard>
                <InsightText>
                  <strong>🎯 Tip:</strong> Consistency beats intensity. Focus on
                  showing up every day, even if it's just for 5 minutes.
                </InsightText>
              </InsightCard>
            </>
          )}

          {!selectedStats && (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <FaChartLine
                size={48}
                style={{ opacity: 0.5, marginBottom: "1rem" }}
              />
              <h3 style={{ margin: "0 0 0.5rem 0" }}>Select a Habit</h3>
              <p style={{ opacity: 0.8, fontSize: "0.9rem" }}>
                Choose a habit from the dropdown to see detailed analytics,
                track your streak, and get personalized insights.
              </p>
            </div>
          )}
        </SidePanel>
      </DashboardGrid>
    </Page>
  );
}
